import moment, { Moment } from 'moment';
import momentTz from 'moment-timezone';
import { Port } from '@api/utils/ports';
import type { DatePickerValue } from '@client/components/DatePicker';
import {
	DATE,
	LaytimeIntervals,
	TimeZoneFormat,
} from './constants';
import { round } from './math';

export const formatDate = (
	date: Moment | Date | string | number | null,
	format = DATE,
	includeTimezoneLabel = true,
	timeZoneFormat?: TimeZoneFormat,
) => {
	if (date == null) {
		return null;
	}

	let m = toMoment(date);

	if (timeZoneFormat === TimeZoneFormat.UTC) {
		m = m.clone().utc();
	} else if (timeZoneFormat === TimeZoneFormat.LocalTime) {
		m = m.clone();
	} else if (timeZoneFormat == null) {
		m = m.clone().utcOffset(m.utcOffset(), true);
	}

	const offset = m.utcOffset();
	const offsetStr = offset === 0 ? 'UTC+00:00' : m.format('Z').replace(/^/, 'UTC');

	if (timeZoneFormat === TimeZoneFormat.UTC) {
		return `${m.format(format)}${includeTimezoneLabel ? ' UTC' : ''}`;
	}

	if (timeZoneFormat === TimeZoneFormat.LocalTime) {
		return includeTimezoneLabel ?
			`${m.format(format)} LT (${offsetStr})` :
			`${m.format(format)}`;
	}

	return m.format(format);
};

export function getUtcOffset(port: Port | null | undefined, date?: Date | Moment): number {
	const timeZone = port?.timezone;

	if (timeZone == null) {
		return 0;
	}

	// Get the current date
	const offset = momentTz.tz(date || Date.now(), timeZone).utcOffset();

	return offset;
}

export const formatDuration = ({
	rawStart,
	rawEnd,
	zeroStr,
	format,
	dayDecimal = 2,
	rawMinutes,
}: {
	rawStart?: Moment | Date;
	rawEnd?: Moment | Date;
	zeroStr?: boolean;
	format?: 'day' | 'hour';
	dayDecimal?: number;
	rawMinutes?: number;
}) => {
	let diffMinutes = 0;

	if (rawMinutes) {
		diffMinutes = rawMinutes;
	} else if (rawStart && rawEnd) {
		const startTime = toMoment(rawStart);
		const endTime = toMoment(rawEnd);
		diffMinutes = toMoment(endTime).diff(startTime, 'minutes');
	}

	const days = Math.floor(diffMinutes / 1440);
	const hours = Math.floor((diffMinutes % 1440) / 60);
	const minutes = round(diffMinutes % 60, 2);

	// Format the duration into "X days, Y hours, Z minutes"
	// Showing only non-zero values, except minutes
	// Yikes
	let result = `
		${days > 0 ? `${days} day${days !== 1 ? 's' : ''},` : ''}
		${hours > 0 ? `${hours} hour${hours !== 1 ? 's' : ''},` : ''}
		${minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}
	`;

	if (format === 'day') {
		const daysWithDecimal = round(diffMinutes / 1440, dayDecimal);
		result = `
				${daysWithDecimal} day${daysWithDecimal !== 1 ? 's' : ''}
		`;
	}

	if (format === 'hour') {
		const hoursWithDecimal = round(diffMinutes / 60, 1);
		result = `
			${hoursWithDecimal} hour${hoursWithDecimal !== 1 ? 's' : ''}
		`;
	}

	if (zeroStr && result.trim() === '') {
		result = `0 ${format ?? 'days'}`;
	}

	// Remove trailing comma
	return result.trim().replace(/,$/, '');
};

export const formatLaytimeDuration = (
	minutes: number,
	interval: LaytimeIntervals,
	decimals = 1,
) => {
	const duration = round(interval === LaytimeIntervals.HOUR ? (minutes / 60) : ((minutes / 60) / 24), decimals);
	const intervalStr = `${interval}${duration === 1 ? '' : 's'}`;

	return `${duration} ${intervalStr}`;
};

export const calculateFractionalDuration = (
	rawStart: Moment | Date,
	rawEnd: Moment | Date,
	fraction: number,
): number => {
	const startTime = toMoment(rawStart);
	const endTime = toMoment(rawEnd);

	const totalMs = endTime.diff(startTime);

	return totalMs * fraction;
};

export const formatFractionalDuration = (
	rawStart: Moment | Date,
	rawEnd: Moment | Date,
	fraction: number,
	zeroStr?: boolean,
	format?: 'day' | 'hour',
	dayDecimal = 2,
) => {
	const startTime = toMoment(rawStart);
	const fractionalMs = calculateFractionalDuration(rawStart, rawEnd, fraction);
	const fractionalEnd = toMoment(startTime).add(fractionalMs, 'milliseconds');

	return formatDuration({ rawStart: startTime, rawEnd: fractionalEnd, zeroStr, format, dayDecimal });
};

export const dateIsInPeriod = (
	date: Moment,
	periodFrom: Moment | string,
	periodTo: Moment | string,
	unit: moment.unitOfTime.StartOf = 'day',
) => (
	date.isSameOrAfter(periodFrom, unit) &&
	date.isSameOrBefore(periodTo, unit)
);

// A period should be an array containing [from, to]
export const dateIsInOneOfPeriods = (
	date: Moment,
	periods: [[Moment, Moment]],
	unit: moment.unitOfTime.StartOf = 'day',
) => periods.some(
	([start, end]) => dateIsInPeriod(date, start, end, unit),
);

/* eslint-disable no-restricted-syntax */
export const toMoment = (input: Moment | Date | string | number) => {
	if (input == null) {
		throw new Error('Function \'toMoment\' was called with nullish input. Perhaps you meant to use \'nowMoment\'?');
	}

	if (moment.isMoment(input)) {
		return moment(input);
	}

	if (input instanceof Date) {
		return moment.parseZone(input.toJSON());
	}

	return moment.parseZone(input);
};

export const nowMoment = (...args: []) => {
	// @ts-ignore
	if (args.length > 0) {
		throw new Error('Function \'nowMoment\' was called with arguments. Perhaps you meant to use \'toMoment\'?');
	}

	return moment.utc();
};
/* eslint-enable no-restricted-syntax */

/** Parses DIS / DA-Desk ISO date strings for Sequelize DATEONLY columns; blank or null → null. */
export const momentFromDisDateString = (value: string | null): Moment | null => {
	if (value == null || value.trim() === '') return null;

	return toMoment(value);
};

type StringifyDatePickerType =
	| Moment |
	Date |
	[Moment | undefined, Moment | undefined] |
	DatePickerValue |
	string |
	undefined;

export const stringifyIfMoment = (value: StringifyDatePickerType): string | null => {
	if (Array.isArray(value)) {
		return value.map(stringifyIfMoment).join(', ');
	}

	if (value == null) {
		return null;
	}

	if (moment.isMoment(value)) {
		return value.seconds(0).milliseconds(0).toISOString(true);
	}

	if (value instanceof Date || typeof value === 'string') {
		const newMoment = toMoment(value).seconds(0).milliseconds(0);

		if (!newMoment.isValid() && !(value instanceof Date)) {
			return value;
		}

		return newMoment.toISOString(true);
	}

	return value;
};

export const formatDateYMD = (date: Moment) => date.format('YYYY-MM-DD');
